import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { setFallbackTitle } from "../utils/pageTitle";
import { QuickSearch } from "./QuickSearch";
import { SHELL, navPaperSx } from "../theme";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Chip,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  AccountCircle,
  DataObject as ObjectIcon,
  Functions as FunctionIcon,
  People as PeopleIcon,
  ShowChart as ChartIcon,
  Payment as PaymentIcon,
  Science as ScienceIcon,
  FactCheck as TestsIcon,
  History as HistoryIcon,
  Folder as FolderIcon,
  VpnKey as VpnKeyIcon,
  AutoAwesome as WizardIcon,
  Code as CodeIcon,
  ChatBubble as ChatIcon,
  Home as HomeIcon,
  RestartAlt as ResetIcon,
  AccountTree as ChainIcon,
  SupportAgent as SupportAgentIcon,
  TipsAndUpdates as TipsIcon,
  ExpandMore as ExpandMoreIcon,
  SmartToy as StudioIcon,
  MonitorHeart as HealthIcon,
  Route as RouteIcon,
  Search as SearchIcon,
  Insights as InsightsIcon,
  Lock as LockIcon,
  Settings as SettingsIcon,
  FilterAlt as FunnelIcon,
  NotificationsActive as AlertIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { UseCasesModal } from "./UseCasesModal";
import { AssistantChatPanel } from "./AssistantChatPanel";
import { isAdminUser } from "../utils/auth";
import { useViewMode, ViewMode } from "../contexts/ViewModeContext";
import { ViewSwitcherTutorial } from "./ViewSwitcherTutorial";
import ImpersonationBanner from "./ImpersonationBanner";
import AnnouncementBanner from "./AnnouncementBanner";
import { useSystemConfig } from "../hooks/useSystemConfig";
import { useTestsAccess } from "../services/testsApi";

const drawerWidth = 240;

const navItemSx = {
  mx: 1,
  borderRadius: 2,
  '&.Mui-selected': {
    bgcolor: 'rgba(56, 189, 248, 0.16)',
    '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: SHELL.navAccent },
  },
  '&.Mui-selected:hover': { bgcolor: 'rgba(56, 189, 248, 0.22)' },
};

interface NavMenuItem {
  section?: string;
  text: string;
  icon: React.ReactNode;
  path: string;
  description: string;
  requiresSubscription?: boolean;
  requiresTests?: boolean;
}

// The same nav in every workspace; the top tabs only change what Home shows.
const menuItems: NavMenuItem[] = [
  {
    section: "MAIN",
    text: "Home",
    icon: <HomeIcon />,
    path: "/dashboard",
    description: "Home for the workspace picked in the top bar",
  },
  {
    section: "BUILD",
    text: "Build Guide",
    icon: <WizardIcon />,
    path: "/dashboard/guide",
    description: "Not sure where to start? Describe your idea and get walked through building it",
    requiresSubscription: true,
  },
  {
    section: "BUILD",
    text: "Function Builder",
    icon: <FunctionIcon />,
    path: "/dashboard/ai-functions",
    description: "Build an AI function by describing it, or fill in the form yourself",
  },
  {
    section: "BUILD",
    text: "Data & Outputs",
    icon: <ObjectIcon />,
    path: "/dashboard/objects",
    description: "The output formats your functions return. Build them by hand or generate with AI",
  },
  {
    section: "LIBRARY",
    text: "Function Library",
    icon: <FunctionIcon />,
    path: "/dashboard/ai-functions-list",
    description: "Browse all your AI functions and endpoints",
  },
  {
    section: "LIBRARY",
    text: "Knowledge Hub",
    icon: <FolderIcon />,
    path: "/dashboard/materials",
    description: "Upload documents and files for your AI to reference",
  },
  {
    section: "LIBRARY",
    text: "Test Library",
    icon: <ScienceIcon />,
    path: "/dashboard/test-materials",
    description: "Sample files and test cases for checking your AI functions",
  },
  {
    section: "LIBRARY",
    text: "Tests",
    icon: <TestsIcon />,
    path: "/dashboard/tests",
    description: "How accurate your functions and chains are, and which model fits each best",
    requiresTests: true,
  },
  {
    section: "ANALYTICS",
    text: "Runs & Performance",
    icon: <HistoryIcon />,
    path: "/dashboard/function-runs",
    description: "Every run, plus speed, reliability and cost for each function and chain",
  },
  {
    section: "ANALYTICS",
    text: "Insights",
    icon: <InsightsIcon />,
    path: "/dashboard/insights",
    description: "Errors, AI credit spend and self-improvement impact across your account",
  },
  {
    section: "ANALYTICS",
    text: "Request Lookup",
    icon: <SearchIcon />,
    path: "/dashboard/request-lookup",
    description: "Find every run for a platform request ID",
  },
  {
    section: "ANALYTICS",
    text: "Model Status",
    icon: <HealthIcon />,
    path: "/dashboard/models",
    description: "Health, failures and incidents for the AI models your functions use",
  },
  {
    section: "SETTINGS",
    text: "API Keys",
    icon: <VpnKeyIcon />,
    path: "/dashboard/api-keys",
    description: "Manage keys to connect your AI functions to external apps",
  },
  {
    section: "SETTINGS",
    text: "Billing & Plan",
    icon: <PaymentIcon />,
    path: "/dashboard/subscription",
    description: "Manage your subscription and view usage",
  },
];

/** Tools kept out of the side nav; reachable from Cmd+K and the pages that use them. */
const searchOnlyPages = [
  {
    text: "Object Generator",
    path: "/dashboard/objects/generate",
    description: "Describe an object or paste example JSON, and get a ready-to-use output format",
  },
  {
    text: "HTML Editor",
    path: "/dashboard/html-editor",
    description: "Visual drag-and-drop editor for HTML pages",
  },
];

/** What Home shows in each workspace, used as its page title. */
const WORKSPACE_HOME_TITLES: Record<ViewMode, string> = {
  developer: "Dashboard",
  studio: "Studio",
  chatbot_generation: "Chatbots",
  chaining: "Function Chains",
};

/** Pages that belong to one workspace, so landing on them selects its tab. */
const routeWorkspace = (pathname: string): ViewMode | null => {
  if (pathname.startsWith("/dashboard/playground")) return "studio";
  if (pathname.startsWith("/chatbot/studio/")) return "chatbot_generation";
  return null;
};

const adminItems = [
  { text: "Customer Hub", icon: <PeopleIcon />, path: "/dashboard/admin/customer-hub" },
  { text: "Customer Management", icon: <PeopleIcon />, path: "/dashboard/admin/customers" },
  { text: "Usage Dashboard", icon: <ChartIcon />, path: "/dashboard/admin/usage" },
  { text: "Self-improvement", icon: <TipsIcon />, path: "/dashboard/admin/learning" },
  { text: "Platform Routes", icon: <RouteIcon />, path: "/dashboard/admin/platform-routes" },
  { text: "Platform Insights", icon: <InsightsIcon />, path: "/dashboard/admin/insights" },
  { text: "Build Guide Funnel", icon: <FunnelIcon />, path: "/dashboard/admin/build-guide" },
  { text: "Alert Emails", icon: <AlertIcon />, path: "/dashboard/admin/alerts" },
  { text: "System Settings", icon: <SettingsIcon />, path: "/dashboard/admin/system" },
];

// Detail pages highlight the nav item they belong to.
const NAV_PARENTS: [string, string][] = [
  ['/dashboard/playground', '/dashboard'],
  ['/chatbot/studio/', '/dashboard'],
  ['/dashboard/tool-runner/', '/dashboard/ai-functions-list'],
  ['/dashboard/objects/', '/dashboard/objects'],
  ['/dashboard/function-runs/', '/dashboard/function-runs'],
  ['/dashboard/run/', '/dashboard/function-runs'],
  ['/dashboard/chain-run/', '/dashboard/function-runs'],
  ['/dashboard/chain-performance/', '/dashboard/function-runs'],
  ['/dashboard/tests/', '/dashboard/tests'],
  ['/dashboard/admin/customers/', '/dashboard/admin/customers'],
];

const isNavSelected = (pathname: string, itemPath: string) =>
  pathname === itemPath || NAV_PARENTS.some(([prefix, parent]) => parent === itemPath && pathname.startsWith(prefix));

const platformAdminItems = [
  { text: "Customers", icon: <PeopleIcon />, path: "/dashboard/platform/customers" },
  { text: "Usage", icon: <ChartIcon />, path: "/dashboard/platform/usage" },
  { text: "Billing", icon: <PaymentIcon />, path: "/dashboard/platform/billing" },
];

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [useCasesOpen, setUseCasesOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantMinimized, setAssistantMinimized] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isPlatformAdmin, permissions, isPublicMode, setIsPublicMode, getEffectiveCustomerTag, hasActiveSubscription, isImpersonating, getIdToken } = useAuth();
  const { viewMode, setViewMode } = useViewMode();
  const { features } = useSystemConfig();
  const { enabled: testsEnabled } = useTestsAccess(getIdToken);
  const navItems = menuItems.filter(i => !i.requiresTests || testsEnabled);

  useEffect(() => {
    const workspace = routeWorkspace(location.pathname);
    if (workspace && workspace !== viewMode) setViewMode(workspace);
  }, [location.pathname, viewMode, setViewMode]);

  useEffect(() => {
    if (location.pathname === '/dashboard' || routeWorkspace(location.pathname)) {
      setFallbackTitle(WORKSPACE_HOME_TITLES[routeWorkspace(location.pathname) ?? viewMode]);
      return;
    }
    const items = [...menuItems, ...searchOnlyPages, ...adminItems];
    const match = items.find(i => i.path === location.pathname)
      || items.find(i => i.path !== '/dashboard' && isNavSelected(location.pathname, i.path));
    setFallbackTitle(match?.text || null);
  }, [location.pathname, viewMode]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {

    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap sx={{ color: '#f1f5f9' }}>
          {getEffectiveCustomerTag()}
          {isPublicMode && (
            <Chip 
              label="PUBLIC MODE" 
              size="small" 
              color="warning" 
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
      </Toolbar>
      <List sx={{ pt: 0 }}>
        {(() => {
          const sections = new Map<string, NavMenuItem[]>();
          navItems.forEach(item => {
            if (item.path === '/dashboard/guide' && !features.build_guide && !isPlatformAdmin) return;
            const section = item.section || 'OTHER';
            if (!sections.has(section)) {
              sections.set(section, []);
            }
            sections.get(section)?.push(item);
          });

          return Array.from(sections.entries()).map(([sectionName, sectionItems], sectionIndex) => (
            <Box key={sectionName}>
              {sectionIndex > 0 && <Divider sx={{ my: 1 }} />}
              {sectionName !== 'OTHER' && sectionName !== 'MAIN' && sectionItems.length > 0 && (
                <ListSubheader
                  component="div"
                  disableSticky
                  sx={{
                    backgroundColor: 'transparent',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    pl: 2,
                    py: 0.25,
                    mb: 0.25,
                  }}
                >
                  {sectionName}
                </ListSubheader>
              )}
              {sectionItems.map((item) => {
                const requiresSubscription = item.requiresSubscription || ['API Keys', 'Runs & Performance', 'Insights', 'Request Lookup', 'Model Status', 'Test Library', 'Tests', 'Knowledge Hub'].includes(item.text);
                const isDisabled = requiresSubscription && !hasActiveSubscription;
                
                const tooltipTitle = (
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                      {item.description}
                    </Typography>
                    {isDisabled && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, fontWeight: 600, color: '#fbbf24' }}>
                        Subscribe to unlock this feature
                      </Typography>
                    )}
                  </Box>
                );
                
                return (
                  <Tooltip key={item.text} title={tooltipTitle} arrow placement="right" enterDelay={600}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={isNavSelected(location.pathname, item.path)}
                        onClick={() => {
                          if (isDisabled) {
                            navigate('/dashboard/subscription');
                          } else {
                            navigate(item.path);
                          }
                        }}
                        disabled={false}
                        sx={{
                          ...navItemSx,
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          py: 0.75,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, opacity: isDisabled ? 0.5 : 1 }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{ sx: { fontSize: '0.92rem', fontWeight: 500 } }}
                        />
                        {isDisabled && <LockIcon sx={{ fontSize: 16, color: SHELL.navMuted }} />}
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                );
              })}
            </Box>
          ));
        })()}
      </List>
      
      {/* Show admin menu for users with admin user_type. While
          impersonating, hide the entire Admin Tools section — the
          server-side block_during_impersonation dependency 403s
          every admin route anyway, so showing the nav would just
          set the operator up for confusing failures. */}
      {user && permissions && isAdminUser(permissions.userType) && !isImpersonating && (
        <>
          <Divider sx={{ my: 1 }} />
          <List
            subheader={
              <ListSubheader component="div" disableSticky>
                Admin Tools
              </ListSubheader>
            }
          >
            {adminItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={isNavSelected(location.pathname, item.path)}
                  onClick={() => navigate(item.path)}
                  sx={navItemSx}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      {/* Platform Admin section hidden for now - TODO pages */}
      {false && isPlatformAdmin && (
        <>
          <Divider sx={{ my: 1 }} />
          <List
            subheader={
              <ListSubheader component="div" disableSticky>
                Platform Admin
              </ListSubheader>
            }
          >
            {platformAdminItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={isNavSelected(location.pathname, item.path)}
                  onClick={() => navigate(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      <Box sx={{ flexGrow: 1 }} />
      
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>

      <Divider />
      
      {/* Legal Links and Badge Section - AT THE VERY BOTTOM */}
      <Box sx={{ px: 2, pb: 2, pt: 2 }}>
        {/* Legal Links */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 1,
            mb: 2,
            alignItems: 'center'
          }}
        >
          <Typography 
            component="a" 
            href="https://www.passio.ai/terms-of-use" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              fontSize: '0.75rem',
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { 
                color: 'primary.main',
                textDecoration: 'underline'
              }
            }}
          >
            Terms of Service
          </Typography>
          <Typography 
            component="a" 
            href="https://www.passio.ai/privacy-policy" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              fontSize: '0.75rem',
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { 
                color: 'primary.main',
                textDecoration: 'underline'
              }
            }}
          >
            Privacy Policy
          </Typography>
        </Box>

        {/* There's an AI for That Badge */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <a 
            href="https://theresanaiforthat.com/ai/platform-admin-portal/?ref=featured&v=440175" 
            target="_blank" 
            rel="nofollow noreferrer"
            style={{ 
              display: 'block',
              width: '100%',
              maxWidth: '180px'
            }}
          >
            <img 
              src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600"
              alt="Featured on There's an AI for That"
              style={{ 
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          </a>
        </Box>
      </Box>
    </div>
  );

  const isPlayground = location.pathname.startsWith("/dashboard/playground");

  return (
    <Box sx={{ display: "flex" }}>
      {/* Fixed red banner shown only while the active session is
          an admin "Sign in as customer" impersonation. The banner
          publishes its measured height into the CSS variable
          `--imp-banner-height` on document.documentElement, which
          we consume below to push the AppBar's `top` and the main
          content's top padding down by exactly that amount. The
          banner is responsive (taller on mobile because the inner
          Stack flips to column orientation), so a measured value
          is more robust than a hard-coded constant. */}
      <ImpersonationBanner />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: SHELL.bg,
          borderBottom: `1px solid ${SHELL.border}`,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          // 0px when not impersonating (banner clears the var on
          // unmount), banner height when impersonating.
          top: 'var(--imp-banner-height, 0px)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ mr: 2 }}>
            AI Platform Portal
          </Typography>
          {/* View Mode Toggle — stays left, next to the title */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode: ViewMode | null) => {
                if (newMode) {
                  setViewMode(newMode);
                  navigate(newMode === 'studio' ? '/dashboard/playground' : '/dashboard');
                }
              }}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                '& .MuiToggleButton-root': {
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  px: 2.25,
                  py: 0.85,
                  minWidth: 88,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(56, 189, 248, 0.22)',
                    color: '#e0f2fe',
                    '&:hover': {
                      bgcolor: 'rgba(56, 189, 248, 0.32)',
                    }
                  }
                }
              }}
            >
              <ToggleButton value="developer">
                <Tooltip title="Build AI functions and call them from your own code.">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <CodeIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.72rem', lineHeight: 1, fontWeight: 500 }}>
                      Developer
                    </Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="studio">
                <Tooltip title="Chat with any model like ChatGPT, or generate live apps with EasyAI and NutritionAI.">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <StudioIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.72rem', lineHeight: 1, fontWeight: 500 }}>
                      Studio
                    </Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="chatbot_generation">
                <Tooltip title="Create chatbots that answer from your knowledge and embed them on a website.">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <ChatIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.72rem', lineHeight: 1, fontWeight: 500 }}>
                      Chatbots
                    </Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="chaining">
                <Tooltip title="Connect functions into multi-step workflows.">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <ChainIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.72rem', lineHeight: 1, fontWeight: 500 }}>
                      Chains
                    </Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            
            {/* Utility icons — grouped after the mode toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 2 }}>
              <QuickSearch
                pages={[
                  ...navItems,
                  ...searchOnlyPages,
                  ...(isAdminUser(permissions?.userType) && !isImpersonating ? adminItems : []),
                ]}
              />
              <Tooltip title="Reset dashboard preference and choose again">
                <IconButton
                  size="small"
                  onClick={() => {
                    localStorage.removeItem('viewMode');
                    localStorage.removeItem('hasCompletedOnboarding');
                    window.location.reload();
                  }}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                    width: 32, height: 32,
                  }}
                >
                  <ResetIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Ask the AI assistant for help">
                <IconButton
                  onClick={() => { setAssistantOpen(true); setAssistantMinimized(false); }}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
                    width: 32, height: 32,
                  }}
                >
                  <SupportAgentIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Tips & examples">
                <IconButton
                  onClick={() => setUseCasesOpen(true)}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
                    width: 32, height: 32,
                  }}
                >
                  <TipsIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Spacer pushes user area to the far right */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Right-side: public toggle + user chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

            {/* Public mode (admin only) */}
            {user && (permissions?.isAdmin || isPlatformAdmin) && (
              <Tooltip title={isPublicMode ? 'Public mode ON — click to switch back' : 'Use as public customer'}>
                <Switch
                  checked={isPublicMode}
                  onChange={(e) => { setIsPublicMode(e.target.checked); window.location.reload(); }}
                  color="warning"
                  size="small"
                />
              </Tooltip>
            )}

            {/* User chip — email + tag + logout in one compact element */}
            {user && (
              <>
                <Chip
                  icon={<AccountCircle />}
                  label={getEffectiveCustomerTag()}
                  deleteIcon={<ExpandMoreIcon />}
                  onDelete={(e: any) => setAnchorEl(e.currentTarget)}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isPublicMode ? '#ff9800' : 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontWeight: 600,
                    border: isPublicMode ? '1px solid #ff9800' : '1px solid rgba(255,255,255,0.3)',
                    '& .MuiChip-icon': { color: 'rgba(255,255,255,0.8)' },
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiChip-label': { textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' },
                    '&:hover': { bgcolor: isPublicMode ? '#fb8c00' : 'rgba(255,255,255,0.25)' },
                  }}
                  size="small"
                />
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem disabled>
                    <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
            </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              // Same banner offset as the AppBar / main content —
              // the drawer paper is `position: fixed` by default
              // and would otherwise have its top hidden behind the
              // impersonation banner.
              top: 'var(--imp-banner-height, 0px)',
              height: 'calc(100% - var(--imp-banner-height, 0px))',
              ...navPaperSx,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              top: 'var(--imp-banner-height, 0px)',
              height: 'calc(100% - var(--imp-banner-height, 0px))',
              ...navPaperSx,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isPlayground ? 0 : 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          pt: isPlayground
            ? 'var(--imp-banner-height, 0px)'
            : 'calc(var(--imp-banner-height, 0px) + 24px)',
          display: isPlayground ? 'flex' : 'block',
          flexDirection: 'column',
          height: isPlayground ? 'calc(100vh - var(--imp-banner-height, 0px))' : undefined,
          minHeight: 0,
          overflow: isPlayground ? 'hidden' : undefined,
        }}
      >
        <Toolbar />
        {isPlayground ? (
          <Box sx={{ flex: 1, minHeight: 0, px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column' }}>
            {children}
          </Box>
        ) : (
          <>
            <AnnouncementBanner />
            {children}
          </>
        )}
      </Box>
      <UseCasesModal open={useCasesOpen} onClose={() => setUseCasesOpen(false)} />
      <ViewSwitcherTutorial />
      <AssistantChatPanel
        open={assistantOpen}
        minimized={assistantMinimized}
        onClose={() => setAssistantOpen(false)}
        onMinimize={() => setAssistantMinimized(true)}
        onExpand={() => setAssistantMinimized(false)}
      />
    </Box>
  );
}
